import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";
import { SocketContextType } from "../types";

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const socketUrl =
      process.env.REACT_APP_SOCKET_URL || "http://localhost:5000";
    const newSocket = io(socketUrl, {
      transports: ["websocket", "polling"],
    });

    newSocket.on("connect", () => {
      // Connected to server
      setIsConnected(true);
    });

    newSocket.on("disconnect", () => {
      // Disconnected from server
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      // Connection error
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const joinDashboard = () => {
    if (socket) {
      socket.emit("join-dashboard");
    }
  };

  const leaveDashboard = () => {
    if (socket) {
      socket.emit("leave-dashboard");
    }
  };

  const value: SocketContextType = {
    socket,
    isConnected,
    joinDashboard,
    leaveDashboard,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
